import { useCallback, useRef, useState } from 'react';

/**
 * Pull-to-refresh hook for dashboard pages.
 * Works on both mobile touch and desktop (mouse drag).
 * Returns: { pullRef, isPulling, pullDistance, onRefresh handlers }
 */
export function usePullToRefresh(onRefresh, { threshold = 80, maxPull = 120 } = {}) {
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const pulling = useRef(false);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const scrollTop = containerRef.current?.scrollTop ?? window.scrollY;
    if (scrollTop > 5) return; // Only allow pull when at top
    startY.current = e.touches[0].clientY;
    pulling.current = true;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!pulling.current) return;
    const currentY = e.touches[0].clientY;
    const diff = Math.max(0, currentY - startY.current);
    const dampened = Math.min(maxPull, diff * 0.5);
    if (dampened > 10) {
      setIsPulling(true);
      setPullDistance(dampened);
    }
  }, [maxPull]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;

    if (pullDistance >= threshold && onRefresh) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } catch (_) {}
      setIsRefreshing(false);
    }

    setIsPulling(false);
    setPullDistance(0);
  }, [pullDistance, threshold, onRefresh]);

  const pullProps = {
    ref: containerRef,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };

  return { pullProps, isPulling, isRefreshing, pullDistance, threshold };
}
